/* Kurskalender – samler kursøkter fra Meety på tvers av kurs. Booking håndteres av Meety. */
(() => {
  if (customElements.get('kurs-kalender')) return;

  const API = 'https://app.meetyapp.io/api/extension';
  const TZ = 'Europe/Oslo';
  const CACHE_MS = 5 * 60 * 1000;
  const VIEW_KEY = 'kurskalender:view';
  const MODAL = '.meety-modal-content-main';

  const fmtMonth = new Intl.DateTimeFormat('nb-NO', {timeZone: TZ, month: 'long', year: 'numeric'});
  const fmtWeekday = new Intl.DateTimeFormat('nb-NO', {timeZone: TZ, weekday: 'short'});
  const fmtDay = new Intl.DateTimeFormat('nb-NO', {timeZone: TZ, day: 'numeric', month: 'short'});
  const fmtLongDay = new Intl.DateTimeFormat('nb-NO', {timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long'});
  const fmtTime = new Intl.DateTimeFormat('nb-NO', {timeZone: TZ, hour: '2-digit', minute: '2-digit'});
  const fmtDateKey = new Intl.DateTimeFormat('en-CA', {timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit'});
  const WEEKDAYS = ['man', 'tir', 'ons', 'tor', 'fre', 'lør', 'søn'];

  // 'YYYY-MM-DD' i Oslo-tid
  const dateKey = (date) => fmtDateKey.format(date);
  const monthKeyOf = (key) => key.slice(0, 7);
  const shiftMonth = (monthKey, delta) => {
    const [y, m] = monthKey.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  };
  // Midt på dagen i UTC gir riktig måned/dag uansett tidssone ved formatering.
  const monthDate = (monthKey) => new Date(`${monthKey}-15T12:00:00Z`);

  const cacheGet = (key) => {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const {at, data} = JSON.parse(raw);
      return Date.now() - at < CACHE_MS ? data : null;
    } catch (e) {
      return null;
    }
  };
  const cacheSet = (key, data) => {
    try {
      sessionStorage.setItem(key, JSON.stringify({at: Date.now(), data}));
    } catch (e) {}
  };
  const storedView = () => {
    try {
      return localStorage.getItem(VIEW_KEY);
    } catch (e) {
      return null;
    }
  };
  const storeView = (view) => {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch (e) {}
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Meety svarer 429 ved mange samtidige kall – prøv igjen med litt pause.
  const getJSON = async (path, params, retries = 2) => {
    const res = await fetch(`${API}/${path}?${new URLSearchParams(params)}`);
    if (res.status === 429 && retries > 0) {
      await sleep(800 * (3 - retries));
      return getJSON(path, params, retries - 1);
    }
    if (!res.ok) throw new Error(`Meety ${path} ${res.status}`);
    return res.json();
  };

  const waitFor = async (fn, timeout = 4000) => {
    for (let t = 0; t < timeout; t += 100) {
      const result = fn();
      if (result) return result;
      await sleep(100);
    }
    return null;
  };

  // Kjører oppgaver med begrenset samtidighet.
  const mapLimit = async (items, limit, fn) => {
    const results = new Array(items.length);
    let next = 0;
    const worker = async () => {
      while (next < items.length) {
        const i = next++;
        results[i] = await fn(items[i]);
      }
    };
    await Promise.all(Array.from({length: Math.min(limit, items.length)}, worker));
    return results;
  };

  // Meety legger tjenestelisten på siden som [header, ...flate verdier].
  const servicesFromInitData = () => {
    const compact = window.MeetyInitData && window.MeetyInitData.compactServices;
    if (!Array.isArray(compact) || compact.length < 2) return null;
    const [header, values] = compact;
    const services = [];
    for (let i = 0; i + header.length <= values.length; i += header.length) {
      const row = {};
      header.forEach((key, j) => (row[key] = values[i + j]));
      services.push(row);
    }
    return services;
  };

  const escapeHTML = (s) => String(s).replace(/[&<>"']/g, (c) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'})[c]);

  class KursKalender extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.list = this.querySelector('[data-kk-list]');
      this.cal = this.querySelector('[data-kk-cal]');
      this.courses = new Map(
        [...this.querySelectorAll('[data-kk-course]')].map((el) => [
          el.dataset.productId,
          {
            variantId: Number(el.dataset.variantId),
            title: el.dataset.title,
            url: el.dataset.url,
            price: el.dataset.price,
            color: el.dataset.color,
          },
        ])
      );
      this.sessions = [];
      this.filter = 'all';
      this.view = ['list', 'calendar'].includes(storedView()) ? storedView() : this.dataset.defaultView || 'list';
      this.month = null;
      this.selectedDay = null;

      this.addEventListener('click', (e) => {
        const target = e.target.closest('[data-book], [data-filter], [data-view], [data-cal-nav], [data-day]');
        if (!target || !this.contains(target)) return;
        if (target.dataset.book) this.book(target.dataset.book, target);
        else if (target.dataset.filter) this.setFilter(target.dataset.filter);
        else if (target.dataset.view) this.setView(target.dataset.view, true);
        else if (target.dataset.calNav) this.navigateMonth(Number(target.dataset.calNav));
        else if (target.dataset.day) this.selectDay(target.dataset.day);
      });

      this.setView(this.view, false);
      this.load();
    }

    t(key, n) {
      const text = (this.dataset[key] || '').replace('[n]', n);
      // Entall: «1 plass» / «1 ledig plass»
      return n === 1 ? text.replace(/\bplasser\b/, 'plass').replace(/\bledige\b/, 'ledig') : text;
    }

    get cacheKey() {
      const ids = [...this.courses.keys()].sort().join(',');
      return `kurskalender:${this.dataset.shop}:${ids}:${this.dataset.months}`;
    }

    // ---------- Data ----------

    // Tjenester for kursene i seksjonen. Bruker Meetys data på siden; API er reserve.
    async fetchServices(range) {
      for (let waited = 0; waited < 2000 && !servicesFromInitData(); waited += 100) await sleep(100);
      const local = servicesFromInitData();
      if (local) {
        return local.filter((s) => s.status === 1 && this.courses.has(String(s.sProductId)));
      }

      // Meety utelater tjenester når perioden overstiger tjenestens «book X dager fram»,
      // så vi spør i 30-dagersvinduer og slår sammen.
      const shop = this.dataset.shop;
      const windows = [];
      for (let from = range.fromTime; from < range.toTime; from += 30 * 86400) {
        windows.push({fromTime: from, toTime: Math.min(from + 30 * 86400, range.toTime)});
      }
      const lists = await mapLimit(windows, 3, (w) =>
        getJSON('available-services-v2', {page: 1, limit: 50, ...w, employeeId: 0, shop}).catch(() => ({body: null}))
      );
      if (lists.every((res) => !res.body)) throw new Error('Meety services unavailable');
      const byId = new Map();
      lists.forEach((res) => (res.body || []).forEach((s) => byId.set(s.id, s)));
      return [...byId.values()].filter((s) => this.courses.has(String(s.sProductId)));
    }

    // Økter (start/slutt) per tjeneste – raskt kall, også for lange perioder.
    async fetchDates(services, range) {
      const shop = this.dataset.shop;
      const perService = await mapLimit(services, 3, async (s) => {
        const dates = await getJSON('available-dates-v2', {...range, serviceId: s.id, selectingSlotsData: '', shop});
        return dates.map((d) => ({
          serviceId: s.id,
          productId: String(s.sProductId),
          start: d.fromTime,
          end: d.toTime,
          remaining: null,
          loading: true,
          waitlist: this.dataset.waitlist === 'true',
        }));
      });
      return perService
        .flat()
        .filter((s) => s.start > range.fromTime)
        .sort((a, b) => a.start - b.start);
    }

    // Ledige plasser per økt – ett kort kall per økt, maks 3 samtidig.
    async fetchRemaining(sessions) {
      const shop = this.dataset.shop;
      await mapLimit(sessions, 3, async (s) => {
        try {
          const slots = await getJSON('available-slots', {
            serviceId: s.serviceId,
            fromTime: s.start,
            toTime: s.end,
            quantity: 1,
            numSlots: 1,
            isConnectShop: 0,
            selectingSlotsData: '',
            shop,
          });
          const match = slots.find((slot) => slot.fromTime === s.start);
          s.remaining = match ? match.remaining : 0;
        } catch (e) {
          s.remaining = null;
        }
        s.loading = false;
      });
    }

    async load() {
      const cached = cacheGet(this.cacheKey);
      if (cached) {
        this.sessions = cached;
        this.render();
        this.markLoaded();
        return;
      }

      const now = Math.floor(Date.now() / 1000);
      const months = parseInt(this.dataset.months, 10) || 6;
      const range = {fromTime: now, toTime: now + months * 31 * 86400};

      try {
        const services = await this.fetchServices(range);
        this.sessions = await this.fetchDates(services, range);
        this.render();
        if (this.sessions.length) {
          await this.fetchRemaining(this.sessions);
          this.render();
          if (this.sessions.every((s) => s.remaining !== null)) cacheSet(this.cacheKey, this.sessions);
        }
      } catch (err) {
        console.warn('[kurskalender]', err);
        this.renderError();
      } finally {
        this.markLoaded();
      }
    }

    markLoaded() {
      this.list.setAttribute('aria-busy', 'false');
      this.cal.setAttribute('aria-busy', 'false');
    }

    // ---------- Visning ----------

    statusFor(s) {
      if (s.loading) return {cls: 'pending', label: ''};
      if (s.remaining === null) return {cls: 'unknown', label: ''};
      const few = parseInt(this.dataset.fewSeats, 10) || 3;
      if (s.remaining <= 0) return {cls: 'full', label: this.t('textFull')};
      if (s.remaining <= few) return {cls: 'few', label: this.t('textFew', s.remaining)};
      return {cls: 'open', label: this.t('textSeats', s.remaining)};
    }

    // Kort status til kalenderboksene: «13 ledige», «2 igjen», «Fullt».
    shortStatus(s, status) {
      const text = {open: `${s.remaining} ledige`, few: `${s.remaining} igjen`, full: 'Fullt'}[status.cls];
      return text ? `<span class="kk-cal__seats kk-cal__seats--${status.cls}">${text}</span>` : '';
    }

    visibleSessions() {
      return this.filter === 'all' ? this.sessions : this.sessions.filter((s) => s.productId === this.filter);
    }

    actionFor(s, status) {
      if (status.cls === 'full') return s.waitlist ? this.t('textWaitlist') : '';
      return this.t('textBook');
    }

    describe(s, course, action) {
      const start = new Date(s.start * 1000);
      return `${action}: ${course.title}, ${fmtLongDay.format(start)} kl. ${fmtTime.format(start)}`;
    }

    rowHTML(s, i) {
      const course = this.courses.get(s.productId);
      const start = new Date(s.start * 1000);
      const end = new Date(s.end * 1000);
      const status = this.statusFor(s);
      const full = status.cls === 'full';
      const action = this.actionFor(s, status);
      const weekday = fmtWeekday.format(start).replace('.', '');
      return `
        <li class="kk__row kk__row--${status.cls}" style="--kk-i:${i};--kk-c:var(--kk-c${course.color})">
          <div class="kk__date">
            <span class="kk__weekday">${escapeHTML(weekday)}</span>
            <span class="kk__day">${escapeHTML(fmtDay.format(start))}</span>
          </div>
          <div class="kk__info">
            <a class="kk__title" href="${escapeHTML(course.url)}">${escapeHTML(course.title)}</a>
            <span class="kk__meta">kl. ${fmtTime.format(start)}–${fmtTime.format(end)}</span>
          </div>
          <span class="kk__status kk__status--${status.cls}">${escapeHTML(status.label)}</span>
          <div class="kk__action">
            ${
              action
                ? `<button type="button" class="btn ${full ? 'btn--primary btn--outline' : 'btn--primary btn--solid'} btn--small kk__book" data-book="${s.productId}" data-start="${s.start}" aria-label="${escapeHTML(this.describe(s, course, action))}">${escapeHTML(action)}</button>`
                : ''
            }
          </div>
        </li>`;
    }

    render() {
      this.renderList();
      this.renderCalendar();
    }

    renderList() {
      const sessions = this.visibleSessions();
      if (!sessions.length) {
        this.list.innerHTML = `<p class="kk__message">${escapeHTML(this.t('textEmpty'))}</p>`;
        return;
      }
      let html = '';
      let currentMonth = null;
      sessions.forEach((s, i) => {
        const start = new Date(s.start * 1000);
        const month = monthKeyOf(dateKey(start));
        if (month !== currentMonth) {
          if (currentMonth) html += '</ul></div>';
          currentMonth = month;
          html += `<div class="kk__month"><h3 class="kk__month-title">${escapeHTML(fmtMonth.format(start))}</h3><ul class="kk__rows" role="list">`;
        }
        html += this.rowHTML(s, i);
      });
      html += '</ul></div>';
      // Kun inngangsanimasjon første gang; oppdatering av plasser skal ikke blinke.
      this.list.classList.toggle('kk__list--static', Boolean(this.listRendered));
      this.listRendered = true;
      this.list.innerHTML = html;
    }

    monthBounds() {
      const first = monthKeyOf(dateKey(new Date()));
      const last = this.sessions.length ? monthKeyOf(dateKey(new Date(this.sessions[this.sessions.length - 1].start * 1000))) : first;
      return {first, last: last < first ? first : last};
    }

    initialMonth() {
      const sessions = this.visibleSessions();
      const bookable = sessions.find((s) => s.remaining === null || s.remaining > 0) || sessions[0];
      return bookable ? monthKeyOf(dateKey(new Date(bookable.start * 1000))) : this.monthBounds().first;
    }

    renderCalendar() {
      const {first, last} = this.monthBounds();
      // Automatisk valgt måned oppdateres når ledige plasser er hentet; brukerens valg beholdes.
      if (!this.month || this.monthAuto || this.month < first || this.month > last) {
        this.month = this.initialMonth();
        this.monthAuto = true;
      }
      const month = this.month;

      const byDay = new Map();
      this.visibleSessions().forEach((s) => {
        const key = dateKey(new Date(s.start * 1000));
        if (!byDay.has(key)) byDay.set(key, []);
        byDay.get(key).push(s);
      });

      const [y, m] = month.split('-').map(Number);
      const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
      const offset = (new Date(Date.UTC(y, m - 1, 1)).getUTCDay() + 6) % 7; // mandag = 0
      const today = dateKey(new Date());

      let cells = '';
      for (let i = 0; i < offset; i++) cells += '<div class="kk-cal__cell kk-cal__cell--empty" aria-hidden="true"></div>';
      for (let d = 1; d <= daysInMonth; d++) {
        const key = `${month}-${String(d).padStart(2, '0')}`;
        const sessions = byDay.get(key) || [];
        const classes = ['kk-cal__cell'];
        if (key < today) classes.push('kk-cal__cell--past');
        if (key === today) classes.push('kk-cal__cell--today');
        if (sessions.length) classes.push('kk-cal__cell--has');
        if (key === this.selectedDay) classes.push('kk-cal__cell--selected');

        const chips = sessions
          .map((s) => {
            const course = this.courses.get(s.productId);
            const status = this.statusFor(s);
            const action = this.actionFor(s, status);
            const time = fmtTime.format(new Date(s.start * 1000));
            const label = status.label ? `, ${status.label.toLowerCase()}` : '';
            const inner = `<span class="kk-cal__time">${time}</span><span class="kk-cal__name">${escapeHTML(course.title)}</span>${this.shortStatus(s, status)}`;
            return action
              ? `<button type="button" class="kk-cal__chip kk-cal__chip--${status.cls}" style="--kk-c:var(--kk-c${course.color})" data-book="${s.productId}" data-start="${s.start}" aria-label="${escapeHTML(this.describe(s, course, action) + label)}" title="${escapeHTML(course.title + label)}">${inner}</button>`
              : `<span class="kk-cal__chip kk-cal__chip--${status.cls}" style="--kk-c:var(--kk-c${course.color})" title="${escapeHTML(course.title + label)}">${inner}</span>`;
          })
          .join('');
        const dots = sessions
          .map((s) => `<span class="kk-cal__dot kk-cal__dot--${this.statusFor(s).cls}" style="--kk-c:var(--kk-c${this.courses.get(s.productId).color})"></span>`)
          .join('');
        const dayLabel = fmtLongDay.format(new Date(`${key}T12:00:00Z`));

        cells += `
          <div class="${classes.join(' ')}">
            ${
              sessions.length
                ? `<button type="button" class="kk-cal__daybtn" data-day="${key}" aria-label="${escapeHTML(`${dayLabel}: ${sessions.length} kurs`)}" aria-pressed="${key === this.selectedDay}"><span class="kk-cal__num">${d}</span><span class="kk-cal__dots">${dots}</span></button>`
                : `<span class="kk-cal__num">${d}</span>`
            }
            <div class="kk-cal__chips">${chips}</div>
          </div>`;
      }

      const trailing = (7 - ((offset + daysInMonth) % 7)) % 7; // fyll siste uke så rutenettet blir helt
      for (let i = 0; i < trailing; i++) cells += '<div class="kk-cal__cell kk-cal__cell--empty" aria-hidden="true"></div>';

      const legend = [...this.courses.values()]
        .map((c) => `<span class="kk-cal__legend-item" style="--kk-c:var(--kk-c${c.color})">${escapeHTML(c.title)}</span>`)
        .join('');

      const selected = this.selectedDay && monthKeyOf(this.selectedDay) === month ? byDay.get(this.selectedDay) || [] : [];
      const agenda = selected.length
        ? `<div class="kk-cal__agenda"><h4 class="kk-cal__agenda-title">${escapeHTML(fmtLongDay.format(new Date(`${this.selectedDay}T12:00:00Z`)))}</h4><ul class="kk__rows" role="list">${selected
            .map((s, i) => this.rowHTML(s, i))
            .join('')}</ul></div>`
        : '';

      this.cal.innerHTML = `
        <div class="kk-cal__head">
          <button type="button" class="kk-cal__nav" data-cal-nav="-1" aria-label="Forrige måned" ${month <= first ? 'disabled' : ''}>‹</button>
          <h3 class="kk-cal__month" aria-live="polite">${escapeHTML(fmtMonth.format(monthDate(month)))}</h3>
          <button type="button" class="kk-cal__nav" data-cal-nav="1" aria-label="Neste måned" ${month >= last ? 'disabled' : ''}>›</button>
        </div>
        <div class="kk-cal__grid" role="grid">
          ${WEEKDAYS.map((w) => `<div class="kk-cal__weekday" aria-hidden="true">${w}</div>`).join('')}
          ${cells}
        </div>
        <div class="kk-cal__legend">${legend}<span class="kk-cal__legend-item kk-cal__legend-item--full">${escapeHTML(this.t('textFull'))}</span></div>
        ${agenda}`;
    }

    renderError() {
      const url = this.dataset.fallbackUrl;
      const html = `<p class="kk__message">${escapeHTML(this.t('textError'))}${url ? ` <a href="${escapeHTML(url)}">Se kursdatoene her</a>.` : ''}</p>`;
      this.list.innerHTML = html;
      this.cal.innerHTML = html;
    }

    setView(view, remember) {
      this.view = view === 'calendar' ? 'calendar' : 'list';
      this.list.hidden = this.view !== 'list';
      this.cal.hidden = this.view !== 'calendar';
      this.querySelectorAll('[data-view]').forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.view === this.view)));
      if (remember) storeView(this.view);
    }

    setFilter(value) {
      this.filter = value;
      this.selectedDay = null;
      this.month = null;
      this.querySelectorAll('[data-filter]').forEach((chip) => chip.setAttribute('aria-pressed', String(chip.dataset.filter === value)));
      this.render();
    }

    navigateMonth(delta) {
      this.month = shiftMonth(this.month, delta);
      this.monthAuto = false;
      this.selectedDay = null;
      this.renderCalendar();
    }

    selectDay(key) {
      this.selectedDay = this.selectedDay === key ? null : key;
      this.monthAuto = false;
      this.renderCalendar();
      const agenda = this.cal.querySelector('.kk-cal__agenda');
      if (agenda) agenda.scrollIntoView({block: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    }

    // ---------- Booking via Meety ----------

    // Åpner Meetys eget bookingvindu (samme som «Book en tid»). Faller tilbake til produktsiden.
    async book(productId, trigger) {
      const course = this.courses.get(productId);
      if (!course || trigger.hasAttribute('aria-busy')) return;
      const meety = await this.waitForMeety();
      if (!meety || !course.variantId) {
        window.location.href = course.url;
        return;
      }
      const session = this.sessions.find((s) => s.productId === productId && String(s.start) === trigger.dataset.start);
      trigger.setAttribute('aria-busy', 'true');
      try {
        const open = () => meety.showScheduleModal(course.variantId, {quantity: 1, onClose: () => trigger.focus()});
        open();
        // Første forespørsel etter sidelast kan forsvinne mens Meety laster vinduet – prøv én gang til.
        if (!(await waitFor(() => document.querySelector(MODAL), 1500))) {
          meety.isShowingModal = false;
          open();
          if (!(await waitFor(() => document.querySelector(MODAL), 5000))) throw new Error('Meety-vinduet åpnet seg ikke');
        }
        // Meety kan hoppe tilbake til første ledige dato mens den laster – sjekk og prøv igjen.
        for (let attempt = 0; session && attempt < 3; attempt++) {
          await this.preselect(session);
          await sleep(700);
          if (this.isPreselected(session)) break;
        }
      } catch (err) {
        console.warn('[kurskalender]', err);
        window.location.href = course.url;
      } finally {
        trigger.removeAttribute('aria-busy');
      }
    }

    // Meety har ikke API for å forhåndsvelge dato, så vi blar til riktig måned og velger dag og tid
    // i Meetys datovelger. Feiler noe, står vinduet som vanlig og kunden velger selv.
    async preselect(session) {
      try {
        const root = await waitFor(() => document.querySelector(`${MODAL} .meety-date-picker-header`)?.closest(MODAL), 6000);
        if (!root) return;
        // Meety viser først dagens dato og hopper så til første dato med tider når datoene er lastet.
        // Vent til det har skjedd (tider vises), ellers hopper den tilbake etter at vi har bladd.
        await waitFor(() => root.querySelector('.meety-timeslot-picker-list-item'), 5000);
        const start = new Date(session.start * 1000);
        const targetMonth = fmtMonth.format(start).toLowerCase();
        const monthLabel = () => (root.querySelector('.meety-date-picker-header .meety-inline-stack')?.textContent || '').trim().toLowerCase();

        for (let i = 0; i < 12 && monthLabel() !== targetMonth; i++) {
          const next = root.querySelectorAll('.meety-date-picker-header .meety-icon-button')[1];
          if (!next) return;
          const before = monthLabel();
          next.click();
          if (!(await waitFor(() => monthLabel() !== before, 2000))) return;
        }
        if (monthLabel() !== targetMonth) return;

        const dayNumber = new Intl.DateTimeFormat('en-GB', {timeZone: TZ, day: 'numeric'}).format(start);
        const day = await waitFor(
          () =>
            [...root.querySelectorAll('.meety-date-picker-day')].find(
              (d) => d.textContent.trim() === dayNumber && !d.classList.contains('meety-date-picker-day-supplement') && !d.classList.contains('meety-date-picker-day-disabled')
            ),
          2000
        );
        if (!day) return;
        day.click();

        const time = fmtTime.format(start);
        const slot = await waitFor(() =>
          [...root.querySelectorAll('.meety-timeslot-picker-list-item')].find((item) => item.textContent.trim().startsWith(time))
        );
        if (slot && !slot.classList.contains('meety-timeslot-picker-list-item-selected')) slot.click();
      } catch (err) {
        console.warn('[kurskalender] forhåndsvalg feilet', err);
      }
    }

    isPreselected(session) {
      const root = document.querySelector(MODAL);
      const selected = root && root.querySelector('.meety-timeslot-picker-list-item-selected');
      const title = root && root.querySelector('.meety-timeslot-picker-header-title');
      if (!title) return false;
      const start = new Date(session.start * 1000);
      const day = new Intl.DateTimeFormat('nb-NO', {timeZone: TZ, day: 'numeric', month: 'long'}).format(start).toLowerCase();
      if (!title.textContent.toLowerCase().includes(day)) return false;
      // Fullbookede tider kan ikke velges – der viser Meety venteliste for datoen.
      if (session.remaining !== null && session.remaining <= 0) return true;
      return Boolean(selected) && selected.textContent.trim().startsWith(fmtTime.format(start));
    }

    waitForMeety(timeout = 3000) {
      const ready = () => window.Meety && typeof window.Meety.showScheduleModal === 'function' && window.Meety.started;
      if (ready()) return Promise.resolve(window.Meety);
      return new Promise((resolve) => {
        const startedAt = Date.now();
        const timer = setInterval(() => {
          if (ready() || Date.now() - startedAt > timeout) {
            clearInterval(timer);
            resolve(ready() ? window.Meety : null);
          }
        }, 100);
      });
    }
  }

  customElements.define('kurs-kalender', KursKalender);

  // «Se datoer»-lenker andre steder på siden (f.eks. Kursvelger): filtrer kalenderen og scroll dit.
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-kk-show]');
    const calendar = link && document.querySelector('kurs-kalender');
    if (!calendar || !calendar.courses) return;
    e.preventDefault();
    if (calendar.courses.has(link.dataset.kkShow)) calendar.setFilter(link.dataset.kkShow);
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    calendar.scrollIntoView({behavior: reduce ? 'auto' : 'smooth', block: 'start'});
  });
})();
